import React, { useState, useEffect } from 'react';
import '../../index.css';
import { useAuth } from "../../context/authContext";
import { useToast } from "../../components/ui/use-toast";
import microsoft from '../../images/microsoft-icon.svg';
import { microsoftLogin } from '../../services/auth.service';

const OAuthLoginButtons = ({ isLoggingIn, onLoggingInChange }) => {
    const { toast } = useToast();
    const { setAuthenticated, setUser, user } = useAuth();
    const [isTenantIdOpen, setIsTenantIdOpen] = useState(false);
    const [tenantId, setTenantId] = useState('');

    useEffect(() => {
        const savedTenantId = localStorage.getItem('yvr-auth-tenant-id');
        if (savedTenantId) {
            setTenantId(savedTenantId);
            setIsTenantIdOpen(true);
        }
    }, []);

    const isValidTenantId = (value) => {
        const trimmed = value.trim();
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const domainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
        return guidRegex.test(trimmed) || domainRegex.test(trimmed);
    };

    const handleMicrosoftLogin = async () => {
        const trimmedTenantId = tenantId.trim();

        if (trimmedTenantId) {
            if (!isValidTenantId(trimmedTenantId)) {
                toast({
                    title: "Invalid Tenant ID",
                    description: "Please enter a valid GUID (e.g. 12345678-1234-1234-1234-123456789abc) or domain (e.g. your-company.onmicrosoft.com).",
                    variant: "destructive",
                });
                return;
            }
            localStorage.setItem('yvr-auth-tenant-id', trimmedTenantId);
        } else {
            localStorage.removeItem('yvr-auth-tenant-id');
        }

        onLoggingInChange?.(true);
        try {
            await microsoftLogin(setAuthenticated, setUser, user, toast, trimmedTenantId);
        } finally {
            onLoggingInChange?.(false);
        }
    };

    return (
        <div className="oauth-buttons text-center grid grid-cols-1">
            <button
                onClick={handleMicrosoftLogin}
                disabled={isLoggingIn}
                className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-none font-semibold text-gray-700 px-6 py-4 flex flex-row disabled:opacity-60 disabled:cursor-not-allowed">
                <img src={microsoft} alt="" className="mr-3"/>
                <span className="w-48 text-center">Sign in with Microsoft</span>
            </button>

            <button
                type="button"
                onClick={() => setIsTenantIdOpen(!isTenantIdOpen)}
                disabled={isLoggingIn}
                className="text-sm text-[#2E2E2E] hover:opacity-70 hover:underline cursor-pointer flex items-center gap-1 mx-auto mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <span>Specify Tenant ID</span>
                <svg
                    className={`w-4 h-4 transition-transform duration-200 ${isTenantIdOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            <div
                className={`overflow-hidden w-52 mx-auto transition-all duration-300 ease-in-out ${
                    isTenantIdOpen ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'
                }`}
            >
                <input
                    type="text"
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    placeholder="Enter Tenant ID"
                    disabled={isLoggingIn}
                    className="w-full border border-gray-200 rounded-none mt-2 px-4 py-2 text-gray-700 text-sm text-center focus:outline-none focus:border-blue-400 disabled:opacity-60"
                />
            </div>
        </div>
    );
};

export default OAuthLoginButtons;